import { Link } from 'react-router-dom';
import {
    Shield,
    Lock,
    Server,
    Network,
    FileCheck,
    Eye,
    ArrowRight,
    UserCheck,
    KeyRound,
    Trash2,
} from 'lucide-react';
import { ForecasterLogo } from '../components/ForecasterLogo';
import { FooterSection } from '../components/sections';

const trustPoints = [
    {
        icon: Server,
        title: 'Self-hosted, always',
        description:
            'Forecaster runs entirely inside your Kubernetes cluster. No SaaS relay. No external API calls. Your GPU metrics, workload data, and optimization decisions never leave your environment.',
    },
    {
        icon: Eye,
        title: 'No telemetry',
        description:
            'Zero data exfiltration. Predictions run entirely within your cluster. No phone-home calls or third-party analytics.',
    },
    {
        icon: Trash2,
        title: 'Clean uninstall',
        description:
            'One command: helm uninstall forecaster. All Forecaster resources are removed. Your workloads, deployments, and Kubernetes objects continue running unchanged.',
    },
];

const podSecurityDetails = [
    { label: 'Non-root execution', detail: 'Runs as UID 65532 (nonroot)' },
    { label: 'Read-only filesystem', detail: 'Root filesystem is mounted read-only' },
    { label: 'Dropped capabilities', detail: 'ALL Linux capabilities dropped' },
    { label: 'Seccomp profile', detail: 'RuntimeDefault seccomp profile enforced' },
    { label: 'No privilege escalation', detail: 'allowPrivilegeEscalation: false' },
];

const rbacDetails = [
    { label: 'Least privilege', detail: 'ClusterRole with minimum required permissions' },
    { label: 'Opt-in model', detail: 'Only manages pods with explicit Forecaster annotations' },
    { label: 'Namespace scoping', detail: 'Can be restricted to specific namespaces' },
    { label: 'Audit trail', detail: 'All RBAC actions logged via Kubernetes audit logs' },
];

const networkDetails = [
    { label: 'NetworkPolicy', detail: 'Controlled ingress and egress rules by default' },
    { label: 'No external calls', detail: 'Controller makes zero outbound internet requests' },
    { label: 'Metrics endpoint', detail: 'Prometheus /metrics exposed only within cluster' },
    { label: 'TLS support', detail: 'Encryption in transit for all internal communication' },
];

const complianceItems = [
    {
        status: 'In progress',
        label: 'SOC 2 Type II',
        description: 'Audit in progress. Expected completion Q2 2026.',
    },
    {
        status: 'Ready',
        label: 'GDPR',
        description: 'No personal data processed. Self-hosted architecture ensures data residency compliance.',
    },
    {
        status: 'Ready',
        label: 'HIPAA',
        description: 'Self-hosted deployment keeps PHI within your controlled environment. No BAA required.',
    },
    {
        status: 'Compatible',
        label: 'FedRAMP',
        description: 'Runs in any authorized cloud environment. No external data flows.',
    },
];

const SecurityDetailList = ({ items }: { items: Array<{ label: string; detail: string }> }) => (
    <div className="space-y-3">
        {items.map((item) => (
            <div key={item.label} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-volt-500 mt-2 flex-shrink-0" />
                <div>
                    <span className="text-white text-sm font-medium">{item.label}</span>
                    <span className="text-gray-500 text-sm"> -- {item.detail}</span>
                </div>
            </div>
        ))}
    </div>
);

export const Security = () => {
    return (
        <div className="min-h-screen bg-obsidian-900 text-white antialiased">
            {/* Navbar */}
            <nav className="fixed top-0 inset-x-0 z-50 bg-obsidian-900/80 backdrop-blur-xl border-b border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <ForecasterLogo />
                    <Link
                        to="/trial"
                        className="px-5 py-2.5 bg-volt-500 text-obsidian-900 rounded-lg text-sm font-semibold hover:bg-volt-400 transition-all hover:shadow-volt"
                    >
                        Start Free Trial
                    </Link>
                </div>
            </nav>

            {/* Hero */}
            <section className="pt-32 pb-16 relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-volt-500/5 rounded-full blur-[150px]" />
                <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-volt-500/10 border border-volt-500/20 text-volt-400 mb-6 text-sm font-medium">
                        <Shield className="w-4 h-4" />
                        <span>Enterprise Security</span>
                    </div>
                    <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                        Your cluster. Your data. Your control.
                    </h1>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        Forecaster is self-hosted infrastructure software. Nothing leaves your environment. No SaaS. No external dependencies. No trust required.
                    </p>
                </div>
            </section>

            {/* Trust Model */}
            <section className="pb-20">
                <div className="max-w-6xl mx-auto px-6">
                    <h2 className="text-2xl font-bold text-white mb-8">Trust Model</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {trustPoints.map((point) => (
                            <div
                                key={point.title}
                                className="p-6 rounded-xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-all"
                            >
                                <div className="w-10 h-10 rounded-lg bg-volt-500/10 flex items-center justify-center mb-4">
                                    <point.icon className="w-5 h-5 text-volt-400" />
                                </div>
                                <h3 className="text-white font-bold mb-2">{point.title}</h3>
                                <p className="text-sm text-gray-400 leading-relaxed">{point.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Data Privacy */}
            <section className="pb-20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="p-8 rounded-2xl bg-gradient-to-br from-volt-500/5 to-transparent border border-white/10">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-10 h-10 rounded-lg bg-volt-500/10 flex items-center justify-center flex-shrink-0">
                                <Lock className="w-5 h-5 text-volt-400" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-white mb-2">Data Privacy</h2>
                                <p className="text-gray-400">
                                    Forecaster processes GPU metrics and workload signals to make scaling decisions. All of this happens inside your cluster.
                                </p>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="p-5 rounded-xl bg-obsidian-900/60 border border-white/5">
                                <h4 className="text-white font-semibold mb-3 text-sm uppercase tracking-wide">What Forecaster sees</h4>
                                <ul className="space-y-2">
                                    {[
                                        'GPU performance metrics',
                                        'Pod replica counts and scaling events',
                                        'Request latency distributions',
                                        'Cloud provider pricing signals',
                                    ].map((item) => (
                                        <li key={item} className="flex items-start gap-2 text-sm text-gray-400">
                                            <div className="w-1.5 h-1.5 rounded-full bg-volt-500/60 mt-1.5 flex-shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="p-5 rounded-xl bg-obsidian-900/60 border border-white/5">
                                <h4 className="text-white font-semibold mb-3 text-sm uppercase tracking-wide">What Forecaster never sees</h4>
                                <ul className="space-y-2">
                                    {[
                                        'Request or response payloads',
                                        'Model weights or training data',
                                        'User PII or session data',
                                        'Anything outside annotated namespaces',
                                    ].map((item) => (
                                        <li key={item} className="flex items-start gap-2 text-sm text-gray-400">
                                            <div className="w-1.5 h-1.5 rounded-full bg-gray-600 mt-1.5 flex-shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Access Control */}
            <section className="pb-20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-lg bg-volt-500/10 flex items-center justify-center">
                                    <UserCheck className="w-5 h-5 text-volt-400" />
                                </div>
                                <h2 className="text-2xl font-bold text-white">Access Control</h2>
                            </div>
                            <p className="text-gray-400 mb-6 text-sm leading-relaxed">
                                Forecaster follows Kubernetes RBAC best practices. The controller operates with the minimum permissions needed and only acts on resources explicitly opted in via annotations.
                            </p>
                            <SecurityDetailList items={rbacDetails} />
                        </div>

                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-lg bg-volt-500/10 flex items-center justify-center">
                                    <KeyRound className="w-5 h-5 text-volt-400" />
                                </div>
                                <h2 className="text-2xl font-bold text-white">Pod Security</h2>
                            </div>
                            <p className="text-gray-400 mb-6 text-sm leading-relaxed">
                                Every Forecaster pod runs with hardened security settings enabled by default. No configuration required.
                            </p>
                            <SecurityDetailList items={podSecurityDetails} />
                        </div>
                    </div>
                </div>
            </section>

            {/* Network Security */}
            <section className="pb-20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-volt-500/10 flex items-center justify-center">
                            <Network className="w-5 h-5 text-volt-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-white">Network Security</h2>
                    </div>
                    <p className="text-gray-400 mb-6 text-sm max-w-2xl leading-relaxed">
                        Forecaster does not make outbound internet requests. All communication stays within your cluster network boundary.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                        {networkDetails.map((item) => (
                            <div key={item.label} className="p-5 rounded-xl bg-white/[0.02] border border-white/10">
                                <h4 className="text-white font-medium text-sm mb-1">{item.label}</h4>
                                <p className="text-gray-500 text-sm">{item.detail}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Compliance */}
            <section className="pb-20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-volt-500/10 flex items-center justify-center">
                            <FileCheck className="w-5 h-5 text-volt-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-white">Compliance</h2>
                    </div>
                    <p className="text-gray-400 mb-8 text-sm max-w-2xl leading-relaxed">
                        Because Forecaster is self-hosted and processes no customer data, it simplifies compliance for regulated industries.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                        {complianceItems.map((item) => (
                            <div key={item.label} className="p-5 rounded-xl bg-white/[0.02] border border-white/10">
                                <div className="flex items-center gap-3 mb-2">
                                    <span
                                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                            item.status === 'Ready'
                                                ? 'bg-volt-500/10 text-volt-400'
                                                : item.status === 'In progress'
                                                ? 'bg-yellow-500/10 text-yellow-400'
                                                : 'bg-white/5 text-gray-400'
                                        }`}
                                    >
                                        {item.status}
                                    </span>
                                    <h4 className="text-white font-semibold">{item.label}</h4>
                                </div>
                                <p className="text-gray-500 text-sm">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Audit Logging */}
            <section className="pb-24">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="p-8 rounded-2xl bg-white/[0.02] border border-white/10">
                        <h3 className="text-xl font-bold text-white mb-4">Audit Logging</h3>
                        <p className="text-gray-400 text-sm mb-6 max-w-2xl leading-relaxed">
                            Every scaling decision, policy evaluation, and resource change is logged in structured JSON format. Logs are emitted to stdout and integrate with any Kubernetes log aggregation pipeline you already use.
                        </p>
                        <div className="rounded-lg bg-obsidian-900/80 border border-white/5 p-4 font-mono text-xs text-gray-400 overflow-x-auto">
                            <div className="space-y-1">
                                <div>{'{"level":"info","ts":"2026-02-11T10:30:00Z","msg":"scaling decision","namespace":"ml-inference","deployment":"llm-serving","action":"scale-up","replicas":{"from":4,"to":6},"reason":"predicted demand increase"}'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="pb-24">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <div className="p-10 rounded-2xl bg-gradient-to-br from-volt-500/10 via-volt-500/5 to-transparent border border-volt-500/20">
                        <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                            See the security posture yourself
                        </h2>
                        <p className="text-gray-400 mb-8 max-w-lg mx-auto">
                            Install in 5 minutes. Inspect every pod, RBAC rule, and network policy. Uninstall cleanly if it does not meet your standards.
                        </p>
                        <Link
                            to="/trial"
                            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-volt-500 text-obsidian-900 rounded-lg font-semibold text-lg hover:bg-volt-400 transition-colors group"
                        >
                            Start 14-Day Free Trial
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </div>
            </section>

            <FooterSection />
        </div>
    );
};
